import { useState } from "react";
import type { AiRankingPayload, AiTaskCost } from "@dashboard/shared";
import { AiPanel, AiTile } from "@/components/AiTile";
import { Skeleton } from "@/components/ui/skeleton";

// "Cost per Intelligence Index Task" von artificialanalysis.ai fuer die Top 20: USD je
// Aufgabe, gestapelt nach Token-Art, guenstigstes Modell oben (wie die Vorlage, dort
// links). Horizontal statt Saeulen, weil 20 lange Modellnamen in der schmalen Spalte
// sonst nur schraeg lesbar waeren. Farben = Kategorie-Slots 1-5 der Datenviz-Palette
// in fester Reihenfolge (Validator: CVD- und Normalsicht-Abstaende bestanden; Aqua,
// Gelb, Magenta liegen unter 3:1 auf Weiss -> Summe am Balkenende, Tooltip, Tabelle).
const SEGMENTS: { key: keyof Omit<AiTaskCost, "total">; label: string; short: string; color: string }[] = [
  { key: "answer", label: "Answer", short: "Answer", color: "#2a78d6" },
  { key: "reasoning", label: "Reasoning", short: "Reas.", color: "#eb6834" },
  { key: "cacheWrite", label: "Cache Write", short: "C. Write", color: "#1baf7a" },
  { key: "cacheHit", label: "Cache Hit", short: "C. Hit", color: "#eda100" },
  { key: "input", label: "Input", short: "Input", color: "#e87ba4" },
];

const usd = (n: number) => (n > 0 && n < 0.01 ? "<$0.01" : `$${n.toFixed(2)}`);

type Row = { slug: string; name: string; rank: number; cost: AiTaskCost };

function Tooltip({ row }: { row: Row }) {
  return (
    <div className="w-52 rounded-md border border-border bg-popover p-2.5 text-xs shadow-lg">
      <div className="text-base font-semibold tabular-nums">{usd(row.cost.total)}</div>
      <div className="mb-1.5 truncate text-muted-foreground">
        {row.name} · Platz {row.rank}
      </div>
      {SEGMENTS.map((s) => (
        <div key={s.key} className="flex items-center gap-2 py-px">
          <span className="h-0.5 w-3 shrink-0 rounded-full" style={{ background: s.color }} />
          <span className="w-12 font-semibold tabular-nums">{usd(row.cost[s.key])}</span>
          <span className="text-muted-foreground">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

function costRows(data: AiRankingPayload | null): Row[] {
  return (data?.models ?? [])
    .filter((m): m is typeof m & { cost: AiTaskCost } => m.cost !== null)
    .map((m) => ({ slug: m.slug, name: m.name, rank: m.rank, cost: m.cost }))
    .sort((a, b) => a.cost.total - b.cost.total);
}

/** Kachel rechts neben dem Digest: Spanne billigstes bis teuerstes Top-20-Modell. */
export function AiCostTile({ data, open, onToggle }: { data: AiRankingPayload | null; open: boolean; onToggle: () => void }) {
  const rows = costRows(data);
  const cheap = rows[0];
  const dear = rows[rows.length - 1];
  return (
    <AiTile open={open} onToggle={onToggle} toggleId="cost" title="KI-Kosten">
      <span className="truncate text-xs text-muted-foreground" title="USD je Aufgabe des Intelligence Index, billigstes bis teuerstes Top-20-Modell">
        {cheap && dear ? `${usd(cheap.cost.total)} – ${usd(dear.cost.total)}` : data ? "Noch keine Daten" : "…"}
      </span>
    </AiTile>
  );
}

export function AiCostPanel({ data, onClose }: { data: AiRankingPayload | null; onClose: () => void }) {
  const [asTable, setAsTable] = useState(false);
  const [hover, setHover] = useState<number | null>(null);
  const rows = costRows(data);
  const max = Math.max(...rows.map((r) => r.cost.total), 0);

  return (
    <AiPanel
      panelId="cost"
      title="Kosten je Index-Aufgabe · Top 20"
      description="USD je Aufgabe des Intelligence Index, nach Token-Art · weniger ist besser"
      onClose={onClose}
      actions={
        rows.length > 0 && (
          <button
            type="button"
            onClick={() => setAsTable((v) => !v)}
            className="text-xs text-muted-foreground hover:underline"
          >
            {asTable ? "Diagramm" : "Tabelle"}
          </button>
        )
      }
    >
      {!data ? (
        <Skeleton className="h-80" />
      ) : !rows.length ? (
        <div className="text-sm text-muted-foreground">Noch keine Kostendaten.</div>
      ) : asTable ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs tabular-nums">
            <thead className="text-muted-foreground">
              <tr className="border-b border-border">
                <th className="py-1 pr-2 text-left font-medium">Modell</th>
                <th className="px-1 py-1 text-right font-medium">Gesamt</th>
                {SEGMENTS.map((s) => (
                  <th key={s.key} className="whitespace-nowrap px-1 py-1 text-right font-medium" title={s.label}>
                    {s.short}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.slug} className="border-b border-border last:border-b-0">
                  <td className="max-w-[7rem] truncate py-1 pr-2" title={r.name}>{r.name}</td>
                  <td className="px-1 py-1 text-right font-semibold">{usd(r.cost.total)}</td>
                  {SEGMENTS.map((s) => (
                    <td key={s.key} className="px-1 py-1 text-right">{usd(r.cost[s.key])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {SEGMENTS.map((s) => (
              <span key={s.key} className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-[2px]" style={{ background: s.color }} />
                {s.label}
              </span>
            ))}
          </div>
          <ul className="relative" onMouseLeave={() => setHover(null)}>
            {rows.map((r, i) => (
              <li
                key={r.slug}
                tabIndex={0}
                data-ai-cost-row={r.slug}
                onMouseEnter={() => setHover(i)}
                onFocus={() => setHover(i)}
                onBlur={() => setHover(null)}
                className={`relative grid grid-cols-[minmax(0,9.5rem)_1fr] items-center gap-2 rounded px-1 py-[3px] outline-none ${hover === i ? "bg-muted/70" : ""}`}
              >
                <span className="truncate text-xs">{r.name}</span>
                <span className="flex items-center">
                  {/* Summe braucht Platz am Balkenende -> Balken auf (100 % - 3,5 rem) skaliert */}
                  <span
                    className="flex h-3 overflow-hidden rounded-r-[4px]"
                    style={{ width: `calc(${max ? r.cost.total / max : 0} * (100% - 3.5rem))` }}
                  >
                    {SEGMENTS.map((s, k) => (
                      <span
                        key={s.key}
                        // 2-px-Trennlinie nur bei Segmenten ab ~5 px - bei den billigen
                        // Modellen fraesse sie sonst das ganze Segment weg.
                        className={k < SEGMENTS.length - 1 && r.cost[s.key] / max > 0.02 ? "border-r-2 border-white" : ""}
                        style={{ flexGrow: r.cost[s.key], flexBasis: 0, background: s.color }}
                      />
                    ))}
                  </span>
                  <span className="ml-1.5 text-xs font-medium tabular-nums">{usd(r.cost.total)}</span>
                </span>
                {hover === i && (
                  <div
                    className={`pointer-events-none absolute right-0 z-20 ${i > rows.length - 7 ? "bottom-full mb-1" : "top-full mt-1"}`}
                  >
                    <Tooltip row={r} />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </AiPanel>
  );
}
